/**
 * SEO helpers — canonical URLs, defaults, and site-wide structured data.
 *
 * Pairs with the <Seo> component (components/Seo.tsx). Kept framework-light so
 * the same builders work under the SSG prerender pass planned for Phase 6.
 */
import { tenantConfig } from './tenant'

/** Default meta description when a page doesn't supply its own. */
export const DEFAULT_DESCRIPTION =
  `Compare ${tenantConfig.lenderCount}+ lenders with ${tenantConfig.name}. Home loan calculators, live rates and ` +
  `expert mortgage broking across Australia.`

/** Default social-share image (path from site root; a dedicated 1200×630 OG image is ideal). */
export const DEFAULT_OG_IMAGE = tenantConfig.logoImageUrl

/** Turn a route path into an absolute URL (passes through existing absolute URLs). */
export function absoluteUrl(pathOrUrl = '/'): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`
  return `${tenantConfig.siteUrl}${path}`
}

/**
 * Site-wide business structured data (JSON-LD) — identifies the business as a
 * mortgage broker serving Australia. Rendered once at the app root.
 *
 * NOTE: review / AggregateRating markup is deliberately omitted. Google ignores
 * self-serving review markup on a business's own site and it can trigger a
 * structured-data warning — real star ratings belong on the Google Business
 * Profile, not hand-authored JSON-LD.
 */
export function organizationSchema(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'MortgageBroker',
    '@id': `${tenantConfig.siteUrl}/#organization`,
    name: tenantConfig.name,
    url: tenantConfig.siteUrl,
    logo: absoluteUrl(tenantConfig.logoImageUrl),
    image: absoluteUrl(tenantConfig.logoImageUrl),
    description: DEFAULT_DESCRIPTION,
    areaServed: { '@type': 'Country', name: 'Australia' },
    priceRange: 'Free consultation',
  }
}

/** WebSite structured data (enables the sitelinks search box eligibility). */
export function websiteSchema(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${tenantConfig.siteUrl}/#website`,
    url: tenantConfig.siteUrl,
    name: tenantConfig.name,
    publisher: { '@id': `${tenantConfig.siteUrl}/#organization` },
  }
}

/**
 * FAQPage structured data — lets the FAQ appear as a rich result in Google.
 * Pass the SAME question/answer array the page renders so the markup and the
 * visible content always match (mismatches are a structured-data violation).
 */
export function faqSchema(faqs: { q: string; a: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }
}

/** BreadcrumbList builder for interior pages. `trail` is ordered root→current. */
export function breadcrumbSchema(trail: { name: string; path: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.path),
    })),
  }
}
